import * as React from 'react'
import * as Webcam from 'react-webcam'
import Dropzone from 'react-dropzone'
import { inject, observer } from 'mobx-react'
import { Modal, ModalHeader, ModalBody } from 'reactstrap'
import { CurrentCompanyStore } from '../stores/CurrentCompanyStore'
import { uploadFileToS3Bucket, DropzoneFile } from '../utilities/helpers'

interface Props {
  currentCompanyStore?: CurrentCompanyStore
  isOpen: boolean
  onHide: () => void
  onUpload: (uploadObject: { fileName: string; file: any }) => void
}

interface State {
  isUploading: boolean
  showWebcam: boolean
}

// Reference: http://stackoverflow.com/questions/12883871/how-to-upload-base64-encoded-image-data-to-s3-using-javascript-only
function dataURItoBlob(dataURI: string) {
  const binary = atob(dataURI.split(',')[1])
  const array = []
  for (let i = 0; i < binary.length; i++) {
    array.push(binary.charCodeAt(i))
  }
  return new Blob([new Uint8Array(array)], { type: 'image/jpeg' })
}

@inject('currentCompanyStore')
@observer
export class SecureImageUploadModal extends React.Component<Props, State> {
  webcam: HTMLElement | null = null

  constructor(props: Props) {
    super(props)
    this.state = {
      isUploading: false,
      showWebcam: false,
    }
  }

  hide() {
    this.setState({ isUploading: false })
    this.props.onHide()
  }

  uploadImage = async (file: any, fileExtention: string, contentType: string) => {
    this.setState({ isUploading: true })

    const { fileName, signedUrl } = await this.props.currentCompanyStore!.getSignedImageUploadUrl(
      fileExtention,
      contentType,
    )

    const xhr = uploadFileToS3Bucket(file, signedUrl, contentType)

    xhr.onload = () => {
      this.setState({ isUploading: false })
      if (xhr.status === 200) {
        this.props.onUpload({ fileName, file })
      }
    }
  }

  onDrop = async (files: DropzoneFile[]) => {
    if (files.length === 0) {
      return
    }

    const file = files[0]
    const fileName = file.name
    const fileExtention = fileName.split('.').pop()

    if (!fileExtention) {
      return
    }

    const contentType = file.type

    await this.uploadImage(file, fileExtention, contentType)
  }

  onCapture = async (image: any) => {
    const blob = dataURItoBlob(image)
    const fileExtention = 'jpeg'
    const contentType = 'image/jpeg'
    this.setState({ isUploading: true })

    await this.uploadImage(blob, fileExtention, contentType)
  }

  render() {
    return (
      <Modal isOpen={this.props.isOpen} toggle={() => this.hide()}>
        <ModalHeader toggle={() => this.hide()}>Upload secure image</ModalHeader>
        <ModalBody>
          {this.state.isUploading ? (
            <div>Uploading secure image...</div>
          ) : (
            <div>
              <label>Drag and drop / click to upload</label>
              <p className='text-muted'>Image will upload automatically upon selection</p>
              <br />
              <Dropzone
                className='dropzone cursor-pointer'
                onDrop={(files: DropzoneFile[]) => this.onDrop(files)}
              >
                <a className='m-4 text-center'>Click to upload or drag-and-drops</a>
              </Dropzone>
              <br />
              {this.state.showWebcam ? (
                <>
                  <div>
                    <Webcam
                      ref={(ref: any) => {
                        this.webcam = ref
                      }}
                      audio={false}
                      screenshotFormat='image/jpeg'
                      width='100%'
                      height='auto'
                    />
                  </div>
                  <br />
                  <div>
                    <button
                      className='btn btn-success w-100'
                      onClick={() => {
                        const screenshot = (this.webcam as any).getScreenshot()
                        this.onCapture(screenshot)
                      }}
                    >
                      Capture Webcam
                    </button>
                  </div>
                </>
              ) : (
                <div>
                  <button
                    className='btn btn-light w-100'
                    onClick={() => this.setState({ showWebcam: true })}
                  >
                    Capture using webcam
                  </button>
                </div>
              )}
            </div>
          )}
        </ModalBody>
      </Modal>
    )
  }
}
